import APIError from 'exceptions/APIError';
import AppError from 'exceptions/AppError';
import PmParcel from 'interfaces/PmParcel';
import { getIsDemoAccount } from 'services/globalConfig.services';
import { getShippingAttachmentFetcher } from '../fetchers';
import { ShippingAttachmentRespDTO } from '../fetchers/ShipmentDetails.fetcher.dto';

export const getShippingAttachment = async (
  orgId: number,
  shipmentPk: number,
): Promise<ShippingAttachmentRespDTO> => {
  try {
    const { data: axiosDataResp } = await getShippingAttachmentFetcher(
      orgId,
      shipmentPk,
    );
    return ShippingAttachmentRespDTO.fromApiData(axiosDataResp?.data);
  } catch (error) {
    if (error instanceof APIError) {
      throw new AppError(error.message);
    }
    throw error;
  }
};

export const populateReturnShipmentEventsForDemoAccount = (
  shipment: PmParcel,
): PmParcel => {
  const isDemoAccount = getIsDemoAccount();

  if (!isDemoAccount) {
    return shipment;
  }

  // Generate event times starting from current time and going backwards
  const now = new Date();

  // Helper to create ISO timestamp by subtracting minutes from now
  const createEventTime = (minutesAgo: number) => {
    const eventDate = new Date(now.getTime() - minutesAgo * 60000);
    return eventDate.toISOString();
  };

  return {
    ...shipment,
    latestMixedEvent: {
      id: 34601544319,
      carrier: {
        name: 'DHL Paket',
      },
      eventType: 'Refund successfully processed',
      eventTime: createEventTime(0), // Most recent (now)
      eventLocation: null,
      eventTypeMasterData: {
        key: 'J11',
        analytics: 'regular',
        isConsumerVisible: true,
        eventPhase: { key: 'J' },
      },
      additionalParams: {},
      parsedEventTimeTimezone: 'Etc/GMT-2',
    },
    mixedEvents: [
      {
        id: 34601544319,
        carrier: {
          name: 'DHL Paket',
        },
        eventType: 'Refund successfully processed',
        eventTime: createEventTime(0), // Most recent (now)
        eventLocation: null,
        eventTypeMasterData: {
          key: 'J11',
          analytics: 'regular',
          isConsumerVisible: true,
          eventPhase: { key: 'J' },
        },
        additionalParams: {},
        parsedEventTimeTimezone: 'Etc/GMT-2',
      },
      {
        id: 34601544313,
        carrier: {
          name: 'DHL Paket',
        },
        eventType: 'Quality check processed - items accepted',
        eventTime: createEventTime(15), // 15 minutes ago
        eventLocation: null,
        eventTypeMasterData: {
          key: 'J31',
          analytics: 'regular',
          isConsumerVisible: true,
          eventPhase: { key: 'J' },
        },
        additionalParams: {},
        parsedEventTimeTimezone: 'Etc/GMT-2',
      },
      {
        id: 34601544306,
        carrier: {
          name: 'DHL Paket',
        },
        eventType: 'Successfully delivered',
        eventTime: createEventTime(30), // 30 minutes ago
        eventLocation: null,
        eventTypeMasterData: {
          key: 'H10',
          analytics: 'regular',
          isConsumerVisible: true,
          eventPhase: { key: 'H' },
        },
        additionalParams: {},
        parsedEventTimeTimezone: 'Etc/GMT-2',
      },
      {
        id: 34601544292,
        carrier: {
          name: 'DHL Paket',
        },
        eventType: 'Arrival at final delivery center',
        eventTime: createEventTime(45), // 45 minutes ago
        eventLocation: null,
        eventTypeMasterData: {
          key: 'G11',
          analytics: 'regular',
          isConsumerVisible: true,
          eventPhase: { key: 'G' },
        },
        additionalParams: {},
        parsedEventTimeTimezone: 'Etc/GMT-2',
      },
      {
        id: 34591622811,
        carrier: {
          name: 'DHL Paket',
        },
        eventType: 'At sorting center',
        eventTime: createEventTime(120), // 2 hours ago
        eventLocation: null,
        eventTypeMasterData: {
          key: 'E10',
          analytics: 'regular',
          isConsumerVisible: true,
          eventPhase: { key: 'E' },
        },
        additionalParams: {},
        parsedEventTimeTimezone: 'Etc/GMT-2',
      },
      {
        id: 34578509128,
        carrier: {
          name: 'DHL Paket',
        },
        eventType: 'Parcel dropped off with carrier',
        eventTime: createEventTime(195), // 3 hours 15 minutes ago
        eventLocation: null,
        eventTypeMasterData: {
          key: 'D30',
          analytics: 'regular',
          isConsumerVisible: true,
          eventPhase: { key: 'D' },
        },
        additionalParams: {},
        parsedEventTimeTimezone: 'Etc/GMT-2',
      },
      {
        id: 34578509107,
        carrier: {
          name: 'DHL Paket',
        },
        eventType: 'Shipment information sent to last mile service provider',
        eventTime: createEventTime(270), // 4 hours 30 minutes ago
        eventLocation: null,
        eventTypeMasterData: {
          key: 'C20',
          analytics: 'regular',
          isConsumerVisible: true,
          eventPhase: { key: 'C' },
        },
        additionalParams: {},
        parsedEventTimeTimezone: 'Etc/GMT-2',
      },
      ...(shipment.mixedEvents || []),
    ],
  } as any;
};
